"use client";

import {
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import IImportation from "@/interfaces/importation.interface";
import { ImportationsFiltersMessages } from "@/messages/importations/importations-filter.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { ImportationFilterKey } from "../../types";

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const STATUS_VALUES = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  ERROR: "ERROR",
  EMPTY: "",
} as const;

const SOURCE_TYPE_VALUES = {
  CONTACT: "CONTACT",
  PRODUCT: "PRODUCT",
  EMPTY: "",
} as const;

type Props = {
  messages: ImportationsFiltersMessages;
};

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<IImportation>[]>();

  const getValueFromProperty = (
    property: string,
    filterValues: IFilter<IImportation>[]
  ): IFilter<IImportation> | undefined => {
    const filter = filterValues.find((f) => f.property === property);
    return filter;
  };

  const handleFilterChange = (
    property: ImportationFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const currentFilters = [...prevFilters];

      const existingFilterIndex = currentFilters.findIndex((f) => f.property === property);
      if (existingFilterIndex !== -1) {
        currentFilters.splice(existingFilterIndex, 1);
      }

      const isEmptyValue =
        value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmptyValue) {
        return currentFilters;
      }

      const newFilter: IFilter<IImportation> = {
        property: property as keyof IImportation,
        filterType: filterType,
        value: value,
      };

      return [...currentFilters, newFilter];
    });
  };

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) {
      setValues((prevFilters) =>
        prevFilters.filter(
          (f) =>
            f.property !== "rowDate" ||
            (f.filterType !== FilterType.GreaterThan && f.filterType !== FilterType.LessThan)
        )
      );
      return;
    }

    const startDate = value.startOf("day").toISOString();
    const endDate = value.endOf("day").toISOString();

    setValues((prevFilters) => {
      const otherFilters = prevFilters.filter(
        (f) =>
          f.property !== "rowDate" ||
          (f.filterType !== FilterType.GreaterThan && f.filterType !== FilterType.LessThan)
      );

      return [
        ...otherFilters,
        {
          property: "rowDate",
          value: startDate,
          filterType: FilterType.GreaterThan,
        },
        {
          property: "rowDate",
          value: endDate,
          filterType: FilterType.LessThan,
        },
      ];
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const statusValue = selectedValue === STATUS_VALUES.EMPTY ? null : selectedValue;
    handleFilterChange("status", statusValue, FilterType.Equals);
  };

  const handleSourceTypeChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const sourceTypeValue = selectedValue === SOURCE_TYPE_VALUES.EMPTY ? null : selectedValue;
    handleFilterChange("sourceType", sourceTypeValue, FilterType.Equals);
  };

  const handleClearFilters = () => {
    setValues([]);
  };

  const getDateValue = (): dayjs.Dayjs | null => {
    const dateFilter = values.find(
      (f) => f.property === "rowDate" && f.filterType === FilterType.GreaterThan
    );
    return dateFilter ? dayjs(dateFilter.value as string) : null;
  };

  const renderTextField = (
    name: string,
    label: string,
    property: ImportationFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <Grid size={GRID_SIZES.LARGE}>
      <TextField
        name={name}
        label={label}
        onChange={(e) => handleFilterChange(property, e.target.value, filterType)}
        value={getValueFromProperty(property, values)?.value ?? ""}
        error={!!errors[0] && !!errors[0].value}
        helperText={!!errors[0] && errors[0].value}
        fullWidth
        slotProps={{
          inputLabel: { shrink: true },
        }}
      />
    </Grid>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          {renderTextField("mediaName", messages.fileNameOption, "mediaName")}

          <Grid size={GRID_SIZES.LARGE}>
            <DatePicker
              label={messages.dateOption}
              value={getDateValue()}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  helperText: errors[0]?.value,
                  error: Boolean(errors[0]?.value),
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
              <InputLabel shrink>{messages.typeOption}</InputLabel>
              <Select
                value={String(
                  getValueFromProperty("sourceType", values)?.value ?? SOURCE_TYPE_VALUES.EMPTY
                )}
                onChange={handleSourceTypeChange}
                label={messages.typeOption}
                displayEmpty
                inputProps={{ "aria-label": messages.typeOption }}
              >
                <MenuItem value={SOURCE_TYPE_VALUES.EMPTY}>
                  {messages.selectTypePlaceholder || "Selecionar tipo"}
                </MenuItem>
                <MenuItem value={SOURCE_TYPE_VALUES.CONTACT}>{messages.contactOption}</MenuItem>
                <MenuItem value={SOURCE_TYPE_VALUES.PRODUCT}>{messages.productOption}</MenuItem>
              </Select>
              {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth error={!!errors[0] && !!errors[0].value}>
              <InputLabel shrink>{messages.statusOption}</InputLabel>
              <Select
                value={String(getValueFromProperty("status", values)?.value ?? STATUS_VALUES.EMPTY)}
                onChange={handleStatusChange}
                label={messages.statusOption}
                displayEmpty
                inputProps={{ "aria-label": messages.statusOption }}
              >
                <MenuItem value={STATUS_VALUES.EMPTY}>
                  {messages.selectStatusPlaceholder || "Selecionar status"}
                </MenuItem>
                <MenuItem value={STATUS_VALUES.PENDING}>{messages.pendingOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.PROCESSING}>{messages.processingOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.COMPLETED}>{messages.completedOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.ERROR}>{messages.errorOption}</MenuItem>
              </Select>
              {errors[0] && errors[0].value && <FormHelperText>{errors[0].value}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Grid container spacing={2} mt={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
